import { ColDef, ColumnState, GridApi } from 'ag-grid-community'
import { OrderTerm } from 'api'
import { isNil } from 'lodash'

export enum GridId {
  GridView = 'GridView',
  StagingGrid = 'StagingGrid',
  StagingAssignmentsSubGrid = 'StagingAssignmentsSubGrid',
  InitiatedGrid = 'InitiatedGrid',
  InitiatedGridCounterparty = 'InitiatedGridCounterparty',
  IncomingGrid = 'IncomingGrid',
  InitiatedNegotiationAssignmentsSubGrid = 'InitiatedNegotiationAssignmentsSubGrid',
  IncomingNegotiationAssignmentsSubGrid = 'IncomingNegotiationAssignmentsSubGrid',
  CurvesGrid = 'CurvesGrid',
  DealsGrid = 'DealsGrid',
  RollsGrid = 'RollsGrid',
  RelatedDealsGrid = 'RelatedDealsGrid',
  CollateralBasketGrid = 'CollateralBasketGrid',
  AuditGrid = 'AuditGrid',
  CompletedGrid = 'CompletedGrid',
  TradingEntityAliasGrid = 'TradingEntityAliasGrid',
  TradingEntityAliasesGrid = 'TradingEntityAliasesGrid',
  SalesCoverageGrid = 'SalesCoverageGrid',
  TradeSplitGrid = 'TradeSplitGrid',
  HaircutGrid = 'HaircutGrid',
  MidTradeEventsGrid = 'MidTradeEventsGrid',
  OpenReposGrid = 'OpenReposGrid',
  StructuredGrid = 'StructuredGrid',
  PrimeBrokerDefaultsGrid = 'PrimeBrokerDefaultsGrid',
  AdminOrganisationGrid = 'AdminOrganisationGrid',
  AdminTradingEntityGrid = 'AdminTradingEntityGrid',
  AdminClearingHouseGrid = 'AdminClearingHouseGrid',
  AdminUserGrid = 'AdminUserGrid',
  AdminCollateralGrid = 'AdminCollateralGrid',
  AdminCollateralBasketGrid = 'AdminCollateralBasketGrid',
  AdminFatFingerGrid = 'AdminFatFingerGrid',
  AdminHolidayGrid = 'AdminHolidayGrid',
  AdminIndexGrid = 'AdminIndexGrid',
  AdminTradingControlGrid = 'AdminTradingControlGrid',
  AdminProductGrid = 'AdminProductGrid',
  AdminSectorGrid = 'AdminSectorGrid',
  AdminFxRateGrid = 'AdminFxRateGrid',
  AxeGridInitiator = 'AxeGridAxeGridInitiator',
  AxeGridResponder = 'AxeGridAxeGridResponder',
  AdminIssuerOrganisationGrid = 'AdminIssuerOrganisationGrid'
}

export interface ColumnFilter {
  columnId: string
  filterType: string
  filterData: any
}

export interface ColumnView {
  columnId: string
  width: number
  visible: boolean
}

export interface GroupColumn {
  columnId: string
}

export enum BestPriceType {
  RATE = 'RATE',
  REPO_INTEREST = 'REPO_INTEREST'
}
export interface GridColumnCopySelection {
  columnId: string
  groupingId: string
  isSelected: boolean
}

export interface GridBulkDateUpdateFields {
  startDate: Date | null
  endDate: Date | null
  term: OrderTerm | null
}
export interface GlobalGridSettings {
  bestPriceType: BestPriceType
  showOrgActivity: boolean
  showTerminatedRepos: boolean
  showMaturedCollaterals: boolean
  hideExpiredRejectedInInitiated: boolean
  showOnlyUsersSalesActivity: boolean
  showOnlyEnabledUsers: boolean
  showCancelledAxes: boolean
  showDraftMapping: boolean
  bid: number | null
  bidHaircut: number | null
  offer: number | null
  offerHaircut: number | null
}

export interface GridColumnSettings {
  viewId: string
  viewTitle?: string
  filterModel?: any
  columnState?: readonly ColumnState[]
  filterColumns?: ColumnFilter[]
}

export interface GridToolbarSettings {
  showFilter: boolean
  showGrouping: boolean
  showColumnSelector: boolean
  showDelta: boolean
  showCopySelected: boolean
}

export interface GridSettings {
  name: string
  columnSettings: GridColumnSettings
  collapsedRowKeys?: string[]
  columnCopySelections?: GridColumnCopySelection[]
  bulkDateUpdateFields?: GridBulkDateUpdateFields
}

export interface CustomToolbarComponentProps {
  gridId?: GridId
  gridApi: GridApi | null
  toolbarSettings: GridToolbarSettings
  onToolbarSettingsChanged: (toolbarSettings: GridToolbarSettings) => unknown
}

export type GridSettingsId = string

export interface GridSettingsLookup {
  default: GridSettings
  [settingsId: string]: GridSettings
}

export type GridStore = {
  global: GlobalGridSettings
} & Record<GridId, GridSettingsLookup>

export type ColDefWithIdAndSort = ColDef & {
  colId: string
  sort: 'asc' | 'desc'
}

export const colDefWithIdAndSort = (def: ColDef): def is ColDefWithIdAndSort => !isNil(def.colId) && !isNil(def.sort)
